import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { z } from "zod";
import { assertTenantAdmin } from "@/lib/tenants.functions";
import { getRequest } from "@tanstack/react-start/server";

export type PilotBrief = {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  has_crew_profile: boolean;
};

const SAFE_PROFILE_SCHEMA = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  full_name: z.string(),
  title: z.string().nullable(),
  bio: z.string().nullable(),
  linkedin_url: z.string().nullable(),
  is_published: z.boolean(),
  created_at: z.string(),
  updated_at: z.string(),
});

const CONTACT_SCHEMA = z.object({
  forwarding_phone: z.string().nullable(),
  email: z.string(),
});

export type SafeCrewProfile = z.infer<typeof SAFE_PROFILE_SCHEMA>;
export type CrewContact = z.infer<typeof CONTACT_SCHEMA>;

export type CrewProfile = Omit<SafeCrewProfile, "slug"> & {
  // Null until a crewmate is given a public page address.
  slug: string | null;
  pilot_id: string | null;
  forwarding_phone: string | null;
  email: string;
  status?: string;
};

// Basic in-memory per-IP throttle for the reveal endpoint.
// Cloudflare Workers are stateless, so this is per-instance; still useful
// against casual scraping and accidental bursts.
const REVEAL_WINDOW_MS = 60_000;
const REVEAL_MAX_PER_WINDOW = 10;
const revealHits = new Map<string, number[]>();

function getClientIp(): string {
  const req = getRequest();
  const forwarded = req.headers.get("x-forwarded-for");
  if (forwarded) return forwarded.split(",")[0]?.trim() ?? "unknown";
  return req.headers.get("cf-connecting-ip") ?? req.headers.get("x-real-ip") ?? "unknown";
}

function checkRevealThrottle(ip: string): boolean {
  const now = Date.now();
  const history = revealHits.get(ip) ?? [];
  const recent = history.filter((t) => now - t < REVEAL_WINDOW_MS);
  if (recent.length >= REVEAL_MAX_PER_WINDOW) {
    revealHits.set(ip, recent);
    return false;
  }
  recent.push(now);
  revealHits.set(ip, recent);
  return true;
}

export const getCrewProfile = createServerFn({ method: "GET" })
  .validator((data: unknown) => z.object({ slug: z.string().min(1) }).parse(data))
  .handler(async ({ data }): Promise<SafeCrewProfile | null> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: row, error } = await supabaseAdmin.rpc("crew_profile_by_slug", {
      _slug: data.slug,
    });

    if (error) {
      console.error("crew_profile_by_slug failed", error);
      return null;
    }

    if (!row || row.length === 0) return null;
    return SAFE_PROFILE_SCHEMA.parse(row[0]);
  });

export const revealCrewContact = createServerFn({ method: "POST" })
  .validator((data: unknown) => z.object({ slug: z.string().min(1) }).parse(data))
  .handler(async ({ data }): Promise<CrewContact | { error: string }> => {
    const ip = getClientIp();
    if (!checkRevealThrottle(ip)) {
      return { error: "rate_limited" };
    }

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: row, error } = await supabaseAdmin.rpc("crew_contact_by_slug", {
      _slug: data.slug,
    });

    if (error) {
      console.error("crew_contact_by_slug failed", error);
      return { error: "not_found" };
    }

    if (!row || row.length === 0) return { error: "not_found" };
    return CONTACT_SCHEMA.parse(row[0]);
  });

// Desktop visitors can't use a .vcf download, so they trade an email address
// for the contact details. Tighter throttle than reveal — this one sends mail.
const REQUEST_MAX_PER_WINDOW = 3;
const requestHits = new Map<string, number[]>();

function checkRequestThrottle(ip: string): boolean {
  const now = Date.now();
  const recent = (requestHits.get(ip) ?? []).filter((t) => now - t < REVEAL_WINDOW_MS);
  if (recent.length >= REQUEST_MAX_PER_WINDOW) {
    requestHits.set(ip, recent);
    return false;
  }
  recent.push(now);
  requestHits.set(ip, recent);
  return true;
}

export const requestCrewContactSchema = z.object({
  slug: z.string().min(1).max(80),
  email: z.string().trim().email().max(255),
  // Honeypot — real users never fill this.
  company: z.string().max(0).optional().or(z.literal("")),
  elapsedMs: z.number().int().nonnegative().optional(),
});

export type RequestCrewContactResult = { ok: true } | { error: string };

export const requestCrewContactByEmail = createServerFn({ method: "POST" })
  .validator((data: unknown) => requestCrewContactSchema.parse(data))
  .handler(async ({ data }): Promise<RequestCrewContactResult> => {
    if (data.company) return { ok: true }; // silently drop bots
    if (typeof data.elapsedMs === "number" && data.elapsedMs < 1000) {
      return { ok: true };
    }

    const ip = getClientIp();
    if (!checkRequestThrottle(ip)) return { error: "rate_limited" };

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const [profileRes, contactRes] = await Promise.all([
      supabaseAdmin.rpc("crew_profile_by_slug", { _slug: data.slug }),
      supabaseAdmin.rpc("crew_contact_by_slug", { _slug: data.slug }),
    ]);

    const profileRow = (profileRes.data ?? [])[0];
    const contactRow = (contactRes.data ?? [])[0];
    if (profileRes.error || contactRes.error || !profileRow || !contactRow) {
      return { error: "not_found" };
    }

    const profile = SAFE_PROFILE_SCHEMA.parse(profileRow);
    const contact = CONTACT_SCHEMA.parse(contactRow);

    const { sendCrewContactShare } = await import("@/lib/crew-email.server");
    await sendCrewContactShare({
      slug: profile.slug,
      fullName: profile.full_name,
      title: profile.title,
      phone: contact.forwarding_phone,
      crewEmail: contact.email,
      linkedinUrl: profile.linkedin_url,
      requesterEmail: data.email,
    });

    return { ok: true };
  });

export const listCrewProfiles = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<{ profiles: CrewProfile[] }> => {
    await assertTenantAdmin(context);

    // Active crewmates belong here even before they have a public address,
    // otherwise they vanish from this page entirely.
    const { data: rows, error } = await context.supabase
      .from("crew")
      .select("*")
      .or("slug.not.is.null,status.eq.active")
      .order("full_name", { ascending: true });

    if (error) {
      console.error("listCrewProfiles failed", error);
      throw new Error("Could not load crew profiles.");
    }

    return { profiles: (rows ?? []) as CrewProfile[] };
  });

const upsertSchema = z.object({
  id: z.string().uuid().optional(),
  pilot_id: z.string().uuid().nullable().optional(),
  slug: z
    .string()
    .min(1)
    .max(80)
    .regex(/^[a-z0-9-]+$/i, "Slug must be URL-safe"),
  full_name: z.string().min(1),
  title: z.string().max(120).nullable().optional(),
  bio: z.string().max(2000).nullable().optional(),
  forwarding_phone: z.string().max(80).nullable().optional(),
  email: z.string().email(),
  linkedin_url: z.string().url().max(500).nullable().optional(),
  is_published: z.boolean().optional(),
});

async function canManageCrewProfile(
  context: { supabase: any; userId: string },
  pilotId: string | null,
): Promise<boolean> {
  const tenantId = await (async () => {
    const { data, error } = await context.supabase.rpc("default_tenant_id");
    if (error) throw new Error("Could not resolve tenant.");
    return data as string;
  })();

  const { data: isAdmin, error: adminErr } = await context.supabase.rpc("is_tenant_admin", {
    _user_id: context.userId,
    _tenant_id: tenantId,
  });
  if (!adminErr && isAdmin) return true;

  // Rows with no linked pilot can only be managed by tenant admins.
  if (!pilotId) return false;

  const { data: identity, error: pilotErr } = await context.supabase
    .from("pilot_identities")
    .select("user_id")
    .eq("pilot_id", pilotId)
    .eq("tenant_id", tenantId)
    .eq("user_id", context.userId)
    .maybeSingle();

  if (pilotErr || !identity) return false;
  return true;
}

export const upsertCrewProfile = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((data: unknown) => {
    const parsed = upsertSchema.safeParse(data);
    if (!parsed.success) {
      const first = parsed.error.issues[0];
      const field = first?.path?.join(".") ?? "form";
      throw new Error(
        `Please check the ${field.replace(/_/g, " ")} field: ${first?.message ?? "invalid value"}`,
      );
    }
    return parsed.data;
  })
  .handler(async ({ context, data }): Promise<{ profile: CrewProfile }> => {
    if (data.id) {
      const { data: existing } = await context.supabase
        .from("crew")
        .select("pilot_id")
        .eq("id", data.id)
        .single();
      if (!existing || !(await canManageCrewProfile(context, existing.pilot_id))) {
        throw new Error("You don't have permission to update this profile.");
      }
    } else {
      if (!(await canManageCrewProfile(context, data.pilot_id ?? null))) {
        throw new Error("You don't have permission to create this profile.");
      }
    }

    const payload = {
      ...(data.pilot_id !== undefined ? { pilot_id: data.pilot_id } : {}),
      slug: data.slug.toLowerCase(),
      full_name: data.full_name,
      title: data.title ?? null,
      bio: data.bio ?? null,
      forwarding_phone: data.forwarding_phone ?? null,
      email: data.email,
      linkedin_url: data.linkedin_url ?? null,
      is_published: data.is_published ?? false,
    };

    const { data: row, error } = data.id
      ? await context.supabase
          .from("crew")
          .update(payload as never)
          .eq("id", data.id)
          .select()
          .single()
      : await context.supabase
          .from("crew")
          .insert({ ...payload, status: "active" } as never)
          .select()
          .single();

    if (error) {
      console.error("upsertCrewProfile failed", error);
      throw new Error(
        error.message.includes("unique")
          ? "That slug is already in use."
          : "Could not save profile.",
      );
    }

    return { profile: row as CrewProfile };
  });

export const deleteCrewProfile = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((data: unknown) => z.object({ id: z.string().uuid() }).parse(data))
  .handler(async ({ context, data }): Promise<{ ok: boolean }> => {
    const { data: existing } = await context.supabase
      .from("crew")
      .select("pilot_id")
      .eq("id", data.id)
      .single();

    if (!existing || !(await canManageCrewProfile(context, existing.pilot_id))) {
      throw new Error("You don't have permission to delete this profile.");
    }

    const { error } = await context.supabase.from("crew").delete().eq("id", data.id);
    if (error) {
      console.error("deleteCrewProfile failed", error);
      throw new Error("Could not delete profile.");
    }

    return { ok: true };
  });

export const createCrewFromPilot = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((data: unknown) =>
    z
      .object({
        pilotId: z.string().uuid(),
        slug: z
          .string()
          .min(1)
          .max(80)
          .regex(/^[a-z0-9-]+$/i),
        title: z.string().max(120).nullable().optional(),
        bio: z.string().max(2000).nullable().optional(),
        linkedin_url: z.string().url().max(500).nullable().optional(),
        is_published: z.boolean().optional(),
      })
      .parse(data),
  )
  .handler(async ({ context, data }): Promise<{ profile: CrewProfile }> => {
    await assertTenantAdmin(context);

    const { data: pilot, error: pilotErr } = await context.supabase
      .from("pilots")
      .select("id, first_name, last_name, email, phone")
      .eq("id", data.pilotId)
      .single();

    if (pilotErr || !pilot) throw new Error("Could not load pilot.");

    const payload = {
      pilot_id: pilot.id,
      status: "active",
      slug: data.slug.toLowerCase(),
      first_name: pilot.first_name ?? "",
      last_name: pilot.last_name ?? "",
      full_name: `${pilot.first_name} ${pilot.last_name}`.trim(),
      title: data.title ?? null,
      bio: data.bio ?? null,
      forwarding_phone: pilot.phone,
      email: pilot.email,
      linkedin_url: data.linkedin_url ?? null,
      is_published: data.is_published ?? false,
    };

    const { data: row, error } = await context.supabase
      .from("crew")
      .insert(payload as never)
      .select()
      .single();
    if (error) {
      console.error("createCrewFromPilot failed", error);
      throw new Error(
        error.message.includes("unique")
          ? "That slug is already in use."
          : "Could not create profile.",
      );
    }

    return { profile: row as CrewProfile };
  });

export const linkPilotToUser = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((data: unknown) => z.object({ pilotId: z.string().uuid() }).parse(data))
  .handler(async ({ context, data }): Promise<{ ok: boolean }> => {
    await assertTenantAdmin(context);

    const { data: pilot } = await context.supabase
      .from("pilots")
      .select("tenant_id")
      .eq("id", data.pilotId)
      .maybeSingle();
    if (!pilot) throw new Error("Pilot not found.");

    const { error } = await context.supabase.from("pilot_identities").upsert(
      {
        tenant_id: pilot.tenant_id,
        pilot_id: data.pilotId,
        user_id: context.userId,
        provider: "admin-link",
        email: (context.claims?.email as string | undefined) ?? null,
        phone: null,
      },
      { onConflict: "tenant_id,user_id" },
    );

    if (error) {
      console.error("linkPilotToUser failed", error);
      throw new Error("Could not link pilot to user.");
    }

    return { ok: true };
  });

export const listPilotsBrief = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<{ pilots: PilotBrief[] }> => {
    await assertTenantAdmin(context);

    const { data: rows, error } = await context.supabase
      .from("pilots")
      .select("id, first_name, last_name, email, crew(id, slug)")
      .order("first_name", { ascending: true });

    if (error) {
      console.error("listPilotsBrief failed", error);
      throw new Error("Could not load pilots.");
    }

    return {
      pilots: (rows ?? []).map((p: any) => ({
        id: p.id,
        first_name: p.first_name,
        last_name: p.last_name,
        email: p.email,
        has_crew_profile:
          Array.isArray(p.crew) && p.crew.some((c: { slug: string | null }) => c.slug != null),
      })),
    };
  });
